import { AlertTriangle, Check } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { FlaggedFact } from '@/services/composeService';
import { FLAG_HINT } from './messages';

/** Inline text buttons become 40 px tall on touch screens without changing the desktop line. */
const COARSE_INLINE = '[@media(pointer:coarse)]:inline-flex [@media(pointer:coarse)]:min-h-[40px] [@media(pointer:coarse)]:items-center';

type Props = {
  /** Visible error of the last action (improve or dictation). */
  error: string | null;
  /** Screen-reader progress ("Text wird verbessert"); the toolbar shows it visually. */
  announcement: string | null;
  improved: boolean;
  /** Flagged values that are still in the text. */
  flagged: FlaggedFact[];
  /** Shown when the text no longer fits into one WhatsApp message. */
  tooLongHint: string | null;
  onUndo: () => void;
  onSelectFlag: (value: string) => void;
};

/**
 * The composer's single live region: progress for screen readers, errors,
 * and after „Verbessern“ the quiet line „Verbessert · Rückgängig“ with the
 * values to double-check. Nothing hides the flagged values: an error shows
 * on its own line next to them, never instead of them.
 */
export function ComposeStatus({ error, announcement, improved, flagged, tooLongHint, onUndo, onSelectFlag }: Props) {
  const visible = Boolean(error || improved || tooLongHint);
  return (
    <div
      role="status"
      aria-live="polite"
      className={visible ? 'flex flex-wrap items-center gap-x-2 gap-y-1 px-2.5 pb-2 pt-0.5 text-xs leading-5 sm:px-3' : undefined}
    >
      {announcement && <span className="sr-only">{announcement}</span>}
      {improved && (
        <>
          {/* One unit: „Verbessert · Rückgängig“ never breaks apart on narrow screens. */}
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <Check className="h-3.5 w-3.5 text-brand" aria-hidden="true" />
              Verbessert
            </span>
            <span className="text-muted-foreground/60" aria-hidden="true">
              ·
            </span>
            <button
              type="button"
              onClick={onUndo}
              className={`rounded font-medium text-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${COARSE_INLINE}`}
            >
              Rückgängig
            </button>
          </span>
          {flagged.length > 0 && (
            <TooltipProvider delayDuration={150}>
              <span className="inline-flex items-center gap-1 pl-1 font-medium text-warning-strong">
                <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                Bitte prüfen:
              </span>
              {flagged.map((fact) => (
                <Tooltip key={`${fact.kind}:${fact.value}`}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => onSelectFlag(fact.value)}
                      aria-label={`${fact.value} – ${FLAG_HINT}`}
                      className={`rounded-[4px] bg-warning/15 px-1.5 font-medium text-warning-strong underline decoration-warning decoration-wavy decoration-1 underline-offset-2 transition-colors hover:bg-warning/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${COARSE_INLINE}`}
                    >
                      {fact.value}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">
                    {FLAG_HINT}
                  </TooltipContent>
                </Tooltip>
              ))}
            </TooltipProvider>
          )}
        </>
      )}
      {error && <span className="basis-full text-destructive-strong">{error}</span>}
      {tooLongHint && <span className="basis-full text-destructive-strong">{tooLongHint}</span>}
    </div>
  );
}

export default ComposeStatus;
